import React, { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';
import { BarChart3, ArrowLeft, Star, Moon, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API } from '../utils/helpers';

const COLORS = ['#9333ea', '#a855f7', '#c084fc', '#d8b4fe', '#e9d5ff', '#f3e8ff'];
const ARCANA_COLORS = ['#eab308', '#8b5cf6'];

const StatsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetch(`${API}/user/stats`, { credentials: 'include' })
        .then(res => res.json())
        .then(setStats)
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [user]);

  if (authLoading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" />;

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-white/10 rounded w-48" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="h-64 bg-white/10 rounded-xl" />
              <div className="h-64 bg-white/10 rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!stats || stats.summary?.total_readings === 0) {
    return (
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <BarChart3 className="w-16 h-16 mx-auto mb-4 text-purple-400 opacity-50" />
          <h1 className="text-2xl font-bold text-purple-400 mb-2">Sin estadisticas aun</h1>
          <p className="text-[var(--text-muted)] mb-6">Realiza tu primera lectura para comenzar a ver tus estadisticas.</p>
          <Link to="/tarot" className="btn-gold">Hacer una Lectura</Link>
        </div>
      </div>
    );
  }

  const { summary, readings_by_month, top_signs, top_cards, arcana_distribution, spreads_distribution } = stats;

  // Format month labels
  const formattedMonthlyData = readings_by_month.map(item => ({
    ...item,
    label: new Date(item.month + '-01').toLocaleDateString('es-ES', { month: 'short' })
  }));

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/dashboard" className="btn-ghost" data-testid="stats-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-purple-400 flex items-center gap-2">
              <BarChart3 className="w-6 h-6" /> Mis Estadisticas
            </h1>
            <p className="text-[var(--text-muted)] text-sm">Tu actividad mistica en numeros</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="stats-summary">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="glass-card p-6 text-center"
          >
            <p className="text-3xl font-bold text-purple-400">{summary.total_readings}</p>
            <p className="text-sm text-[var(--text-muted)]">Lecturas Totales</p>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="glass-card p-6 text-center"
          >
            <p className="text-3xl font-bold text-purple-400">{summary.total_cards_drawn}</p>
            <p className="text-sm text-[var(--text-muted)]">Cartas Sacadas</p>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="glass-card p-6 text-center"
          >
            <p className="text-3xl font-bold text-purple-400">{summary.horoscopes_viewed}</p>
            <p className="text-sm text-[var(--text-muted)]">Horoscopos</p>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
            className="glass-card p-6 text-center"
          >
            <p className="text-lg font-bold text-purple-300">{summary.favorite_spread}</p>
            <p className="text-sm text-[var(--text-muted)]">Tirada Favorita</p>
          </motion.div>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Readings by Month */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="glass-card p-6"
            data-testid="chart-readings-month"
          >
            <h3 className="text-lg font-bold text-purple-300 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5" /> Lecturas por Mes
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={formattedMonthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="label" stroke="#9ca3af" fontSize={12} />
                  <YAxis stroke="#9ca3af" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1f1f3a', border: '1px solid #9333ea', borderRadius: '8px' }}
                    labelStyle={{ color: '#e9d5ff' }}
                  />
                  <Line type="monotone" dataKey="count" stroke="#9333ea" strokeWidth={2} dot={{ fill: '#9333ea' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Arcana Distribution */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
            className="glass-card p-6"
            data-testid="chart-arcana"
          >
            <h3 className="text-lg font-bold text-purple-300 mb-4 flex items-center gap-2">
              <Star className="w-5 h-5" /> Arcanos Mayores vs Menores
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={arcana_distribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="count"
                    nameKey="type"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {arcana_distribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={ARCANA_COLORS[index % ARCANA_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1f1f3a', border: '1px solid #9333ea', borderRadius: '8px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Top Zodiac Signs */}
          {top_signs && top_signs.length > 0 && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
              className="glass-card p-6"
              data-testid="chart-signs"
            >
              <h3 className="text-lg font-bold text-purple-300 mb-4 flex items-center gap-2">
                <Moon className="w-5 h-5" /> Signos Mas Consultados
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={top_signs} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis type="number" stroke="#9ca3af" fontSize={12} />
                    <YAxis type="category" dataKey="sign" stroke="#9ca3af" fontSize={12} width={80} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1f1f3a', border: '1px solid #9333ea', borderRadius: '8px' }}
                    />
                    <Bar dataKey="count" fill="#9333ea" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          )}

          {/* Spread Distribution */}
          {spreads_distribution && spreads_distribution.length > 0 && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
              className="glass-card p-6"
              data-testid="chart-spreads"
            >
              <h3 className="text-lg font-bold text-purple-300 mb-4">Tiradas Realizadas</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={spreads_distribution}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="count"
                      nameKey="spread"
                      label={({ name, percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''}
                    >
                      {spreads_distribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1f1f3a', border: '1px solid #9333ea', borderRadius: '8px' }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          )}
        </div>

        {/* Top Cards */}
        {top_cards && top_cards.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
            className="glass-card p-6"
            data-testid="top-cards-section"
          >
            <h3 className="text-lg font-bold text-purple-300 mb-4">Tus Cartas Mas Frecuentes</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-10 gap-4">
              {top_cards.map((card, i) => (
                <motion.div 
                  key={card.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + i * 0.05 }}
                  className="text-center"
                >
                  {card.image ? (
                    <img 
                      src={card.image} 
                      alt={card.name} 
                      className="w-full aspect-[2/3] object-cover rounded-lg border border-gold-300/10 shadow-lg shadow-purple-500/10"
                    />
                  ) : (
                    <div className="w-full aspect-[2/3] rounded-lg border border-gold-300/10 bg-purple-900/20 flex items-center justify-center">
                      <Star className="w-8 h-8 text-purple-400" />
                    </div>
                  )}
                  <p className="text-xs text-[var(--text-secondary)] mt-2 font-medium truncate">{card.name}</p>
                  <p className="text-xs text-purple-400">{card.count} veces</p>
                  <p className={`text-xs ${card.arcana === 'major' ? 'text-yellow-400' : 'text-[var(--text-muted)]'}`}>
                    {card.arcana === 'major' ? 'Mayor' : 'Menor'}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default StatsPage;
